import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Plus, Loader2, Pencil, Trash2, Clock, User, PlaneTakeoff } from 'lucide-react';
import { toast } from 'sonner';
import RouteForm from '../components/RouteForm';
import Sidebar from '../components/Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageSrc = (url) => {
  if (!url) return null;
  if (url.startsWith('/api')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

const DAYS = [
  { key: 'mon', es: 'L', en: 'M' },
  { key: 'tue', es: 'M', en: 'T' },
  { key: 'wed', es: 'X', en: 'W' },
  { key: 'thu', es: 'J', en: 'T' },
  { key: 'fri', es: 'V', en: 'F' },
  { key: 'sat', es: 'S', en: 'S' },
  { key: 'sun', es: 'D', en: 'S' },
];

const Routes = () => {
  const { language } = useLanguage();
  useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, routeId: null });
  const [deleting, setDeleting] = useState(false);

  const fetchRoutes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/routes`);
      setRoutes(response.data);
    } catch (error) {
      console.error('Error fetching routes:', error);
      toast.error(language === 'es' ? 'Error al cargar rutas' : 'Error loading routes');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateString; }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/routes/${deleteDialog.routeId}`);
      toast.success(language === 'es' ? 'Ruta eliminada' : 'Route deleted');
      fetchRoutes();
    } catch {
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, routeId: null });
    }
  };

  if (showForm) {
    return <RouteForm route={editingRoute} onSuccess={() => { setShowForm(false); setEditingRoute(null); fetchRoutes(); }}
      onCancel={() => { setShowForm(false); setEditingRoute(null); }} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />

      <main className="lg:ml-64 pt-14 lg:pt-0 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <div>
              <h1 className="font-barlow text-2xl lg:text-3xl font-bold uppercase tracking-tight text-[#1E3A5F]">
                {language === 'es' ? 'Gestión de Rutas' : 'Route Management'}
              </h1>
              <p className="text-slate-500 mt-1 text-sm">
                {language === 'es' ? `${routes.length} rutas registradas` : `${routes.length} routes registered`}
              </p>
            </div>
            <Button onClick={() => { setEditingRoute(null); setShowForm(true); }}
              className="bg-[#8B1538] hover:bg-[#6D102B] text-white font-medium uppercase tracking-wide gap-2">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">{language === 'es' ? 'Agregar Ruta' : 'Add Route'}</span>
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
            </div>
          ) : routes.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <PlaneTakeoff className="h-16 w-16 text-slate-300 mb-4" />
                <p className="text-slate-500 text-lg">{language === 'es' ? 'No hay rutas registradas' : 'No routes registered'}</p>
                <Button onClick={() => setShowForm(true)} className="mt-4 bg-[#8B1538] hover:bg-[#6D102B] text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Agregar Ruta' : 'Add Route'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {routes.map(item => (
                <Card key={item.id} className="border-slate-200 hover:shadow-md transition-shadow overflow-hidden">
                  <div className="flex flex-col sm:flex-row">
                    <div className="w-full sm:w-40 h-28 flex-shrink-0 bg-slate-100">
                      {item.image ? (
                        <img src={getImageSrc(item.image)} alt={`${item.origin}-${item.destination}`}
                          className="w-full h-full object-cover"
                          onError={e => e.target.parentElement.classList.add('hidden')} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <PlaneTakeoff className="h-8 w-8 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Route code */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-barlow text-xl font-bold text-[#1E3A5F]">{item.origin}</span>
                            <PlaneTakeoff className="h-4 w-4 text-[#8B1538]" />
                            <span className="font-barlow text-xl font-bold text-[#1E3A5F]">{item.destination}</span>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{item.originAirport}</p>
                          <p className="text-xs text-slate-500 truncate">{item.destinationAirport}</p>

                          {/* Schedule */}
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-600">
                            <span className="font-medium">ETD {item.etd}</span>
                            <span>→</span>
                            <span className="font-medium">ETA {item.eta}</span>
                            {item.route && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${item.route === 'DIRECTO' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {item.route}
                              </span>
                            )}
                          </div>

                          {/* Days */}
                          <div className="flex gap-1 mt-2">
                            {DAYS.map(d => (
                              <span key={d.key} className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-medium
                                ${item.days?.[d.key] ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-400'}`}>
                                {language === 'es' ? d.es : d.en}
                              </span>
                            ))}
                          </div>

                          {/* Audit */}
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                            {item.createdBy && <span className="flex items-center gap-1"><User className="h-3 w-3" />{item.createdBy}</span>}
                            {item.createdAt && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{language === 'es' ? 'Creado' : 'Created'}: {formatDate(item.createdAt)}</span>}
                            {item.updatedAt && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{language === 'es' ? 'Actualizado' : 'Updated'}: {formatDate(item.updatedAt)}</span>}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button variant="outline" size="sm" onClick={() => { setEditingRoute(item); setShowForm(true); }}
                            className="text-[#1E3A5F] border-slate-200 hover:bg-slate-50">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteDialog({ open: true, routeId: item.id })}
                            className="text-red-600 border-slate-200 hover:bg-red-50 hover:border-red-200">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={deleteDialog.open} onOpenChange={open => !open && setDeleteDialog({ open: false, routeId: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] font-barlow uppercase tracking-tight">
              {language === 'es' ? 'Eliminar Ruta' : 'Delete Route'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' ? '¿Está seguro de eliminar esta ruta?' : 'Are you sure you want to delete this route?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, routeId: null })} disabled={deleting}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'es' ? 'Eliminar' : 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Routes;
